/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CheckableExpandableSearchFilterGroup, {
  type UnappliedFiltersByFacet,
} from 'bundles/search-common/components/experiments/filters2024/CheckableExpandableSearchFilterGroup';
import FilterChipDropdown from 'bundles/search-common/components/experiments/filters2024/FilterChipDropdown';
import { CourseraPlusFilterChipButton } from 'bundles/search-common/components/experiments/filters2024/components/CourseraPlusFilterChipButton';
import {
  filterValueAdapter,
  userFiltersAdapter,
} from 'bundles/search-common/components/experiments/filters2024/filterValueAdapter';
import {
  FILTER_DROPDOWN_KEYS,
  getCountsPerKey,
  getFilterDropdownPropsMap,
  getFiltersByFacet,
  getSingleFilterValueForKey,
} from 'bundles/search-common/components/experiments/filters2024/utils';
import useSearchFilterConfigs from 'bundles/search-common/components/filters/useSearchFilterConfigs';
import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import { type SearchPageEventingData } from 'bundles/search-common/types';
import { getSearchFilterOptionItemTranslations } from 'bundles/search-common/utils/getFilterItemsTranslations';
import useStableSearchResults from 'bundles/search-common/utils/useStableSearchResults';
import { createFilterKeyToOptionsMap, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

type Props = { eventingData: SearchPageEventingData };

const styles = {
  chipWrapper: css`
    display: flex;
    align-items: center;
    gap: 2px;
  `,
  chipDropdown: css`
    display: flex;
  `,
  desktopPanelWrapper: css`
    min-width: 360px;

    /* the goal is to have the total height of the panel + header + footer be around 416px by default  */
    max-height: 263px;
  `,
  checkboxGroup: css`
    width: 100%;
  `,
};

const scrollOnApply = () => {
  scrollUpIfNecessary({ mustBeScrolledPastThisElementId: 'search-results-header-wrapper' });
};

const getTriggerLabel = (
  filterGroupKey: string,
  filterGroupName: string,
  adaptedUserFilter: string[][] | undefined,
  countsPerCategory: Record<string, number>
): string | undefined => {
  if (countsPerCategory[filterGroupKey] === 1) {
    const singleFilterValue = getSingleFilterValueForKey(filterGroupKey, adaptedUserFilter);
    if (singleFilterValue) {
      const groupTranslations = getSearchFilterOptionItemTranslations()[filterGroupKey];
      const translatedLabel = groupTranslations?.[singleFilterValue];
      if (translatedLabel) {
        return typeof translatedLabel === 'string' ? translatedLabel : translatedLabel.title;
      }
      return singleFilterValue;
    }
  }
  return filterGroupName;
};

export const FilterChipDropdowns = (props: Props): React.ReactElement => {
  const { eventingData } = props;
  const { setFiltersByFacet } = useSearch();
  const searchResults = useStableSearchResults();
  const { facets, facetFilters: userFilters } = searchResults || {};
  const { facets: allFilters } = useStableSearchResults(SEARCH_MAIN_INDEX_ID) ?? {};
  const [unappliedFilters, setUnappliedFilters] = useState<UnappliedFiltersByFacet>();
  const isMobileOrTabletView = useMediaQuery(breakpoints.down('sm'));

  const track = useTracker();

  const { filterGroupConfigs } = useSearchFilterConfigs(FILTER_DROPDOWN_KEYS);

  const filterKeyToValuesMap = React.useMemo(
    () => createFilterKeyToOptionsMap(facets, userFilters, FILTER_DROPDOWN_KEYS, allFilters),
    [allFilters, userFilters, facets]
  );

  const adaptedUserFilter = userFiltersAdapter(userFilters);

  const countsPerCategory = useMemo(() => getCountsPerKey(adaptedUserFilter), [adaptedUserFilter]);
  const handleFiltersChange = (newFilters: UnappliedFiltersByFacet) => setUnappliedFilters(newFilters);
  const dropdownPropsMap = getFilterDropdownPropsMap();

  // This is to initially update the state with any filters that have already been selected
  useEffect(() => {
    if (userFilters) {
      setUnappliedFilters(getFiltersByFacet(userFilters));
    }
  }, [userFilters]);

  return (
    <div css={styles.chipWrapper}>
      {filterGroupConfigs.map((filterGroupConfig) => {
        const filterGroupKey = filterGroupConfig.property;
        const filterGroupName = dropdownPropsMap[filterGroupKey]?.triggerLabel ?? filterGroupConfig.getName();

        if (filterGroupKey === IS_PART_OF_COURSERA_PLUS) {
          const isCPlusActive = getSingleFilterValueForKey(filterGroupKey, userFilters) === 'true';
          return (
            <CourseraPlusFilterChipButton
              key={filterGroupKey}
              isActive={isCPlusActive}
              onClick={() => {
                setFiltersByFacet(
                  SEARCH_MAIN_INDEX_ID,
                  filterGroupKey,
                  isCPlusActive ? [] : [`${filterGroupKey}:true`]
                );
                scrollOnApply();
              }}
            />
          );
        }

        return (
          <div key={filterGroupKey} css={styles.chipDropdown}>
            <FilterChipDropdown
              panelHeader={
                <Typography2 variant="subtitleLarge" component="h3">
                  {filterGroupName}
                </Typography2>
              }
              triggerLabel={getTriggerLabel(filterGroupKey, filterGroupName, adaptedUserFilter, countsPerCategory)}
              triggerCount={countsPerCategory[filterGroupKey] ?? 0}
              onReset={() => {
                setFiltersByFacet(SEARCH_MAIN_INDEX_ID, filterGroupKey, []);
                scrollOnApply();

                track('apply_filter', {
                  filter: [],
                  filterSource: 'dropdown_filter',
                  isReset: true,
                });
              }}
              onApply={() => {
                setFiltersByFacet(SEARCH_MAIN_INDEX_ID, filterGroupKey, unappliedFilters?.[filterGroupKey] ?? []);
                scrollOnApply();

                track('apply_filter', {
                  filter: unappliedFilters?.[filterGroupKey] || [],
                  filterSource: 'dropdown_filter',
                });
              }}
              onClose={() => setUnappliedFilters(getFiltersByFacet(userFilters))}
            >
              <div css={[!isMobileOrTabletView && styles.desktopPanelWrapper]}>
                {!filterKeyToValuesMap[filterGroupKey] && (
                  <Typography2 component="p" variant="bodyPrimary" tabIndex={0}>
                    {_t('No filters available')}
                  </Typography2>
                )}
                <CheckableExpandableSearchFilterGroup
                  groupConfig={{ ...filterGroupConfig, showAllItems: true }}
                  eventingData={eventingData}
                  {...filterValueAdapter(
                    filterGroupKey,
                    filterKeyToValuesMap[filterGroupKey],
                    unappliedFilters?.[filterGroupKey],
                    handleFiltersChange
                  )}
                  isChip
                  shouldHideGroupLabel
                />
              </div>
            </FilterChipDropdown>
          </div>
        );
      })}
    </div>
  );
};

export default FilterChipDropdowns;
